<template>
  <div class="relative flex-1">
    <FormField
      ref="formField"
      :value="queryText"
      :title="$t('search-form.arrival.desktop.title')"
      :placeholder="$t('search-form.arrival.desktop.placeholder')"
      :readonly="!townFromId"
      :is-active="isActive"
      :is-error="isErrorDropdownVisible"
      @onInput="onFieldInput"
      @onClick="onShowDropdown"
      @onFocus="onShowDropdown"
    />
    <DropdownErrorButton
      v-if="isErrorDropdownVisible"
      ref="errorDropdown"
      v-on-click-outside="onClickOutsideErrorDropdownHandler"
      class="mt-2"
      @click="onErrorDropdownClick"
    >
      {{ $t('search-form.validations.required-dep') }}
    </DropdownErrorButton>
    <div
      v-if="!isErrorDropdownVisible && isActive"
      ref="dropdown"
      v-on-click-outside="onClickOutsideHandler"
      class="dropdown-form-shadow absolute z-30 mt-2 flex max-h-[424px] divide-x rounded-xl bg-white"
    >
      <CountryToStep
        class="w-[300px]"
        :query="countryQueryText"
        :town-from-id="townFromId"
        :selected-country-to-id="countryTo?.id"
        @onSelect="onCountryToSelect"
      />
      <TownToStep
        v-if="townFromId && countryTo"
        :town-from-id="townFromId"
        :selected-country-to-id="countryTo.id"
        :initial-towns-to="townsTo"
        class="w-[300px]"
        @onTownsToChange="onTownsToChange"
        @onSkipTownsToStep="$emit('onSkipTownsToStep')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { useTranslation } from 'i18next-vue';
  import { vOnClickOutside } from '@vueuse/components';
  import { first, isNull } from 'lodash-es';
  import type { Tourflow } from '@aviata/platform-types';

  import type { OnOutsideClickHandler } from '@shared/types';

  import FormField from '../FormField.vue';
  import DropdownErrorButton from '../DropdownErrorButton.vue';
  import CountryToStep from './CountryToStep.vue';
  import TownToStep from './TownToStep.vue';

  import { useErrorDropdown } from '../../../model';
  import { SearchFormField } from '../../../types';

  const props = defineProps<{
    isActive: boolean;
    townFromId?: number;
    activeFormField?: SearchFormField;
    countryTo?: Tourflow.CountryTo;
    townsTo: Tourflow.TownTo[] | null;
  }>();

  const emit = defineEmits<{
    onCountryToSelect: [country?: Tourflow.CountryTo];
    onTownsToChange: [townsTo: Tourflow.TownTo[] | null];
    onSkipTownsToStep: [];
    onResetActiveField: [];
    onActiveFieldChange: [field: SearchFormField];
  }>();

  const { t } = useTranslation();

  const dropdown = ref<HTMLElement | null>(null);
  const formField = ref<HTMLElement | null>(null);
  const queryText = ref(getQueryText(props.countryTo, props.townsTo));

  const countryQueryText = computed(
    () => first(queryText.value.split(',')) ?? '',
  );

  watch(
    () => [props.countryTo, props.townsTo],
    ([country, town]) => {
      if (country) {
        queryText.value = getQueryText(country, town);
      }
    },
  );

  const {
    isErrorDropdownVisible,
    showErrorDropdown,
    hideErrorDropdown,
    createOnClickOutsideErrorDropdownHandler,
  } = useErrorDropdown();

  const onClickOutsideErrorDropdownHandler =
    createOnClickOutsideErrorDropdownHandler(formField);

  watch(
    () => props.townFromId,
    (value, oldValue) => {
      if (value !== oldValue) {
        setQueryText();
      }
    },
  );

  watch(
    () => props.activeFormField,
    field => {
      if (field === SearchFormField.ARRIVAL_FORM_FIELD) {
        if (props.townFromId) {
          formField.value?.focus();
        } else {
          emit('onResetActiveField');
          showErrorDropdown();
        }
      }
    },
  );

  function getTownsToText(towns: Tourflow.TownTo[]) {
    if (towns.length === 0) {
      return t('search-form.arrival.all-regions');
    }

    return t('search-form.arrival.count', {
      count: towns.length,
    });
  }

  function getQueryText(
    country?: Tourflow.CountryTo,
    towns?: Tourflow.TownTo[] | null,
  ) {
    if (!country) {
      return '';
    }

    if (!towns) {
      return country.name;
    }

    return `${country.name}, ${getTownsToText(towns)}`;
  }

  function setQueryText(
    country?: Tourflow.CountryTo,
    towns?: Tourflow.TownTo[] | null,
  ) {
    queryText.value = getQueryText(country, towns);
  }

  function onFieldInput(event: Event) {
    const target = event.target as HTMLInputElement;
    queryText.value = target.value;
  }

  function checkSelections() {
    if (isNull(props.townsTo)) {
      emit('onCountryToSelect');
    }
  }

  function onErrorDropdownClick() {
    hideErrorDropdown();
    emit('onActiveFieldChange', SearchFormField.TOWN_TO_FORM_FIELD);
  }

  function onShowDropdown() {
    if (props.townFromId) {
      emit('onActiveFieldChange', SearchFormField.ARRIVAL_FORM_FIELD);
    } else {
      showErrorDropdown();
    }
  }

  function onCountryToSelect(country: Tourflow.CountryTo) {
    emit('onCountryToSelect', country);
    setQueryText(country, props.townsTo);
  }

  function onTownsToChange(towns: Tourflow.TownTo[] | null) {
    emit('onTownsToChange', towns);
    setQueryText(props.countryTo, towns);
  }

  const onClickOutsideHandler: OnOutsideClickHandler = [
    () => {
      checkSelections();
      setQueryText(props.countryTo, props.townsTo);
      emit('onResetActiveField');
    },
    { ignore: [formField] },
  ];
</script>
